import { useState, useEffect, useRef, Fragment } from "react";

export interface MatchResult {
  source: string;
  target: string;
  grade: string;
  /** True when match was fuzzy (initial assessment) */
  fuzzy?: boolean;
}

export interface MatchByComponentItem {
  ourCourse: string;
  ourHours: number;
  externalCourse: string;
  exemptedHours: number;
  grade: string;
  fuzzy?: boolean;
}

export interface MatchesByComponent {
  component: string;
  courses: MatchByComponentItem[];
}

/** When provided, required hours per component (from Step 1) for summary and column */
interface ComponentRequirement {
  component: string;
  requiredHours: number;
}

interface ResultsTableProps {
  matches: MatchResult[];
  unmatched: Array<{ courseName: string; grade: string }>;
  onMatchesChange: (matches: MatchResult[]) => void;
  /** When set, render grouped-by-component table and summary */
  matchesByComponent?: MatchesByComponent[];
  componentRequirements?: ComponentRequirement[];
  totalRequiredHours?: number;
}

export default function ResultsTable({
  matches,
  unmatched,
  onMatchesChange,
  matchesByComponent = [],
  componentRequirements = [],
  totalRequiredHours = 0,
}: ResultsTableProps) {
  const prevMatchesRef = useRef<string>("");
  const matchesKey = JSON.stringify(matches);

  const [editableMatches, setEditableMatches] =
    useState<MatchResult[]>(matches);

  // Update editableMatches when matches prop changes (but avoid infinite loop)
  useEffect(() => {
    // Only update if matches actually changed (by comparing JSON string)
    if (matchesKey !== prevMatchesRef.current) {
      prevMatchesRef.current = matchesKey;
      setEditableMatches([...matches]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesKey]);

  const handleSourceEdit = (index: number, newSource: string) => {
    const updated = [...editableMatches];
    updated[index] = { ...updated[index], source: newSource };
    setEditableMatches(updated);
    onMatchesChange(updated);
  };

  const handleRemove = (index: number) => {
    const updated = editableMatches.filter((_, i) => i !== index);
    setEditableMatches(updated);
    onMatchesChange(updated);
  };

  const useGroupedView =
    matchesByComponent.length > 0 &&
    componentRequirements.length > 0 &&
    totalRequiredHours >= 0;

  // Sum exempted hours once per (component, ourCourse) so summary matches table
  const totalExemptedHours = useGroupedView
    ? matchesByComponent.reduce((sum, g) => {
        const seen = new Set<string>();
        const componentSum = g.courses.reduce((s, c) => {
          const key = `${g.component}:${c.ourCourse}`;
          if (seen.has(key)) return s;
          seen.add(key);
          return s + c.exemptedHours;
        }, 0);
        return sum + componentSum;
      }, 0)
    : 0;

  if (editableMatches.length === 0 && unmatched.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p className="font-medium text-gray-700 mb-2">
          לא הצלחנו לזהות קורסים בגיליון שהעלית.
        </p>
        <p className="mb-2">
          ייתכן שיש בעיה באיכות הסריקה או בפורמט הקובץ.
        </p>
        <p className="mb-2">
          נא לנסות להעלות קובץ אחר או לסרוק מחדש את גיליון הציונים.
        </p>
        <p className="text-sm text-gray-500">
          תומך בקבצי PDF, JPG ו-PNG.
        </p>
      </div>
    );
  }

  if (useGroupedView) {
    return (
      <div className="space-y-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  רכיב לימודים
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  סה&quot;כ ש&quot;ש
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  קורס שלנו
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  קורס שמזכה בפטור
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  כמות ש&quot;ש פטור
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {componentRequirements.map((req) => {
                const group = matchesByComponent.find(
                  (g) => g.component === req.component
                );
                const rawCourses = group?.courses ?? [];
                const seenOurCourse = new Set<string>();
                const courses = rawCourses.filter((c) => {
                  if (seenOurCourse.has(c.ourCourse)) return false;
                  seenOurCourse.add(c.ourCourse);
                  return true;
                });
                const requiredHours = req.requiredHours;
                return (
                  <Fragment key={req.component}>
                    {courses.length === 0 ? (
                      <tr key={req.component} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-right font-medium text-gray-800">
                          {req.component}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">
                          {requiredHours}
                        </td>
                        <td colSpan={3} className="px-4 py-3 text-right text-gray-500 text-sm">
                          אין התאמות
                        </td>
                      </tr>
                    ) : (
                      courses.map((c, i) => (
                        <tr
                          key={`${req.component}-${i}`}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 text-right text-gray-800">
                            {i === 0 ? req.component : ""}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {i === 0 ? requiredHours : ""}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-800">
                            {c.ourCourse} ({c.ourHours} ש&quot;ש)
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {c.externalCourse}
                            {c.fuzzy && (
                              <span className="mr-1 text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded inline-block">
                                הערכה ראשונית
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {c.exemptedHours}
                          </td>
                        </tr>
                      ))
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-right">
          <p className="text-gray-800 font-medium">
            סה&quot;כ היית אמור ללמוד {totalRequiredHours} ש&quot;ש, ומתוכן נחסכו
            לך {totalExemptedHours} ש&quot;ש.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Matched Courses Table */}
      {editableMatches.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3 text-gray-800">
            קורסים מותאמים
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    קורס מהתמליל
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    פטור מוצע
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    ציון
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    פעולה
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {editableMatches.map((match, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <input
                        type="text"
                        value={match.source}
                        onChange={(e) =>
                          handleSourceEdit(index, e.target.value)
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                        dir="rtl"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <input
                        type="text"
                        value={match.target}
                        onChange={(e) => {
                          const updated = [...editableMatches];
                          updated[index] = {
                            ...updated[index],
                            target: e.target.value,
                          };
                          setEditableMatches(updated);
                          onMatchesChange(updated);
                        }}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                        dir="rtl"
                      />
                      {match.fuzzy && (
                        <span className="mr-1 text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded inline-block mt-1">
                          הערכה ראשונית
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                      {match.grade}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleRemove(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        הסר
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
